import {IBundleViewModel} from "./bundle-view-model.interface";
import {IBundleType} from "../../../bundle-types/bundle-type.interface";
import {Price} from "../../../currency/price";
import {BookingModel} from "../booking.model";
import {BundleIncludedSsrModel} from "./bundle-included-ssr.model";
import {BundlePriceModel} from "./bundle-price.model";
import {JourneyModel} from "../journey/journey.model";
import {
    IDotRezBundleAvailability
} from "../../../dot-rez-api/data-contracts/booking/bundles-availability/bundles-availability.data-contracts";
import {ISsrType} from "../../../ssr-types/ssr-types.service.interface";
import {IServiceFactory} from "../../../service-factory.interface";
import {IBundleSsrUpgradeModel, IBundleSsrUpgradePair} from "./bundle-ssr-upgrade.model.interface";
import {BundleSsrUpgradeModel} from "./bundle-ssr-upgrade.model";
import {computed, makeObservable} from "mobx";

export class BundleModel implements IBundleViewModel {
    constructor( public readonly bundleCode: string,
                 public readonly journey: JourneyModel) {


        makeObservable(this, {
            dotRezBundleAvailability: computed,
            bundleType: computed,
            prices: computed
        });
    }

    get shouldShowChildBundleInfo(): boolean {

        if(!this.journey.childBundle) {
            return false;
        }

        if(!this.journey.booking.passengers.some(p => p.passengerType.isChild)) {
            return false;
        }


        return !this.journey.booking.pso.isPsoAcceptedByUser;
    }

    get dotRezBundleAvailability(): IDotRezBundleAvailability {
        const bundleAvailability = this.journey.booking.bundlesAvailability.find(b => b.bundleCode === this.bundleCode);

        if(!bundleAvailability) {
            throw new Error(`Bundle with code ${this.bundleCode} is not available for journey ${this.journey.journeyKey}`)
        }

        return bundleAvailability;

    }


    get bundleType(): IBundleType {
        return this.journey.services.bundleTypes.getBundleType(this.bundleCode);
    }
    get prices(): BundlePriceModel[] {
        return this._buildBundlePrices();
    }

    get booking(): BookingModel {
        return this.journey.booking;
    }

    get services(): IServiceFactory {
        return this.booking.services;
    }


    get isSelected(): boolean {
        if(!this.booking.departureJourney) {
            return false
        }


        if(this.journey.selectedBundle?.bundleCode === this.bundleCode) {
            return true;
        }

        if(this.booking.departureJourney.selectedBundle) {
            return this.bundleCode === this.booking.departureJourney.selectedBundle.bundleCode;
        } else {
            return false;
        }
    }

    get pricePerPassenger(): Price {
        //This will work as long as the bundle has the same price for all passenger types;
        return this.createPrice(Math.max(0, this.prices[0].price.amount - (this.journey.getDefaultInitialBundlePrice()?.amount || 0)));
    }


    get includedSsrs(): BundleIncludedSsrModel[] {
        return this.prices[0].includedSsrs;
    }

    createPrice(amount: number): Price {
        return this.booking.createPrice(amount);
    }

    get hasSeatIncluded(): boolean {
        return this.includedSsrs.some(ssr => this.booking.services.ssrTypes.isSeat(ssr.currentSsrType));
    }

    async upgradeBundleSsrs(ssrUpgrade: IBundleSsrUpgradeModel): Promise<void> {
        let ssrsUpgradesPairs = this.getCurrentSsrsUpgradesPairs().filter(pair => pair.fromSsrType.ssrCode !== ssrUpgrade.fromSsrType.ssrCode);
        ssrsUpgradesPairs.push({
            fromSsrType: ssrUpgrade.fromSsrType,
            toSsrType: ssrUpgrade.toSsrType
        })
        await this.journey.upgradeBundleSsrs(ssrsUpgradesPairs);

    }

    async downgradeBundleSsr(ssrToDowngrade: BundleIncludedSsrModel): Promise<void> {
        const remainingUpgrades: BundleSsrUpgradeModel[] = [];
        for(let includedSsr of this.includedSsrs) {
            if(includedSsr.originalSsrType.ssrCode === ssrToDowngrade.originalSsrType.ssrCode) {
                continue;
            }

            if(includedSsr.upgradedToSsrType) {
                const availableUpgrade = includedSsr.getAvailableUpgrade(includedSsr.upgradedToSsrType);
                if(availableUpgrade) {
                    remainingUpgrades.push(availableUpgrade);
                }
            }
        }

        await this.journey.upgradeBundleSsrs(remainingUpgrades);

    }

    getSsrsAvailableUpgrades(ssrsUpgradePairs: IBundleSsrUpgradePair[]): IBundleSsrUpgradeModel[] {
        const result: IBundleSsrUpgradeModel[] = [];

        for(let ssrsPair of ssrsUpgradePairs) {
            const includedSsr = this.includedSsrs.find(ssr => ssr.originalSsrType.ssrCode === ssrsPair.fromSsrType.ssrCode);
            if(includedSsr) {
                const availableUpgrade = includedSsr.getAvailableUpgrade(ssrsPair.toSsrType);
                if(availableUpgrade) {
                    result.push(availableUpgrade);
                }
            }
        }

        return result;
    }

    getCurrentSsrsUpgradesPairs(): IBundleSsrUpgradePair[] {
        const result: IBundleSsrUpgradePair[] = [];
        for(let includedSsr of this.includedSsrs) {
            if(includedSsr.upgradedToSsrType) {
                result.push({
                    fromSsrType: includedSsr.originalSsrType,
                    toSsrType: includedSsr.upgradedToSsrType
                });
            }
        }
        return result;
    }

    private _buildBundlePrices(): BundlePriceModel[] {
        const availableBundlePrices = this.dotRezBundleAvailability.pricesByJourney.toDictionary(item => item.key)[this.journey.journeyKey].value.prices;
        return availableBundlePrices.map(bundlePrice => {

            //TODO - uncomment this in order to put back SSR in bundle upgrade
            //const upgradableSsrs = (bundlePrice.upgradableSsrs ?? []).groupByKey(upSsr => upSsr.upgradeFromSsrCode);
            const includedSsrsTypes = this.bundleType.appendVirtualSsrs(bundlePrice.includedSsrs.map(s => s.ssrCode))
                .map(ssrCode => this.booking.getSSR(ssrCode));

            const includedSsrsAliases = this._getSsrsAliases(includedSsrsTypes);

            const createBundleIncludedSsrModel = (ssrType: ISsrType) => {
                const aliases = includedSsrsAliases[ssrType.ssrCode] ?? [];
                const upgrades: BundleSsrUpgradeModel[] = [];
                //TODO - uncomment this in order to put back SSR in bundle upgrade
                //const upgrades = (upgradableSsrs[ssrType.ssrCode] ?? []).map(dotRezSsrUpgrade => new BundleSsrUpgradeModel(ssrType, dotRezSsrUpgrade, this));
                return new BundleIncludedSsrModel(ssrType, this, aliases, upgrades);
            }


            return new BundlePriceModel(this.createPrice(bundlePrice.totalPrice),
                                        bundlePrice.passengerType,
                                        includedSsrsTypes.map(createBundleIncludedSsrModel))
        });
    }

    private _getSsrsAliases(ssrsTypes: ISsrType[]): Record<string, ISsrType[]> {
        const ssrsAliases: Record<string, ISsrType[]> = {};

        for(let includedSsrType of ssrsTypes) {
            if(includedSsrType.aliasFor) {
                if(!ssrsAliases[includedSsrType.aliasFor.ssrCode]) {
                    ssrsAliases[includedSsrType.aliasFor.ssrCode] = [];
                }

                ssrsAliases[includedSsrType.aliasFor.ssrCode].push(includedSsrType)
            }
        }
        return ssrsAliases;
    }



}




